import { ReactNode, createContext, useState } from "react";
import useDisclosure from "../hooks/use-disclosure";
import { useReminder } from "../pages/app/provider/patient/use-Reminder";
import ReminderModal from "../components/reminder/ReminderModal";
import { useTriggerAppointmentReminder } from "../components/reminder/use-trigger-appointment-reminder";

interface Reminder {
  triggerFutureAppEvent: () => void;
}

const reminderContext = createContext<Reminder | null>(null);

/**
 * @description Owns the future-appointment fetch and the reminder timer,
 *   so the modal stays globally available and re-arms whenever the
 *   `["future-appointment"]` query is invalidated.
 *
 * @author Rajkumar Rathod
 */
export const ReminderProvider = ({ children }: { children?: ReactNode }) => {
  const { triggerFutureAppEvent, futureAppointmentDetail } =
    useTriggerAppointmentReminder();

  const urlParams = new URLSearchParams(window.location.search);
  const urlRoomId = urlParams.get("room");

  const triggeredTime = futureAppointmentDetail?.startTime
    ? new Date(futureAppointmentDetail.startTime)
    : null;

  const reminderDisclosure = useDisclosure();

  // Snapshot of the appointment that triggered the reminder. The live
  // `futureAppointmentDetail` is wiped by the refetch below (it advances to the
  // next appointment, or `null`), so the open modal must read from this frozen
  // copy instead of the query value.
  const [reminderAppointment, setReminderAppointment] = useState<any>(null);

  useReminder({
    remindTime: triggeredTime,
    onTriggerEvent: () => {
      if (futureAppointmentDetail?.room !== urlRoomId) {
        setReminderAppointment(futureAppointmentDetail);
        reminderDisclosure.open();
      }
      // Refresh so the next future appointment (if any) re-arms the timer.
      triggerFutureAppEvent();
    },
  });

  return (
    <reminderContext.Provider value={{ triggerFutureAppEvent }}>
      <ReminderModal
        disclosureHost={reminderDisclosure}
        appointment={reminderAppointment}
      />
      {children}
    </reminderContext.Provider>
  );
};
